import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Modal, Spin, message } from "antd";
import { AlertTriangle, Loader, RefreshCw, ShieldCheck } from "lucide-react";
import { AN_COLORS, AN_FONT } from "../../../design-system/tokens";
import { card, FieldLabel, InlineValue, Note, PanelHead } from "../../../pages/enduser/ssh-proxy/sshProxyUi";
import { getPasswordSsh, passwordErrorText, setPasswordSsh } from "../../../service/sshProxyService";
import { isAdminUser } from "../../../utils/helpers";
import { AccountsInput } from "./CaTrustPanel";

// How password SSH reaches an endpoint, and the accounts that may still log
// in with a password directly. Opened from the endpoint details menu
// ("Password SSH exceptions…"); the accounts themselves are in its Local users
// table. setPasswordSsh is administrator only; passwordSsh is not. A
// decommissioned endpoint is shown read-only.

// ─── Password SSH ────────────────────────────────────────────────────────────

// The backend's rule (handler/instances/password_ssh.go): at most 8 names,
// the portable username set, no leading '-', ≤ 32 characters, and never root,
// authnull-svc or nobody.
export const MAX_PASSWORD_SSH_ALLOW = 8;
const NEVER_PASSWORD_SSH = ["root", "authnull-svc", "nobody"];
export function passwordSshAllowError(names) {
  if (names.length > MAX_PASSWORD_SSH_ALLOW) return `At most ${MAX_PASSWORD_SSH_ALLOW} accounts can keep password SSH`;
  for (const n of names) {
    if (NEVER_PASSWORD_SSH.includes(n)) return `"${n}" can never keep password SSH`;
    if (n.length > 32) return `"${n}" is longer than 32 characters`;
    if (n.startsWith("-")) return `"${n}" must not start with '-'`;
    if (!/^[A-Za-z0-9._-]+$/.test(n)) return `"${n}" — use letters, digits, '.', '_' or '-'`;
  }
  return null;
}

// How long a saved exception list takes to reach the machine: the save queues
// an account scan, which writes the sshd rule.
const APPLY_WAIT_MS = 60000;

const PASSWORD_SSH_DESC =
  "Every managed endpoint accepts password SSH only from its gateway, and never for root. The gateway applies the rule at each account scan.";

const rowStyle = { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", fontSize: 13, fontFamily: AN_FONT };

function Chip({ tone = "warn", children }) {
  const t =
    tone === "warn"
      ? { bg: "var(--an-warnBg)", bd: "var(--an-warnBd)", fg: "var(--an-warnFg)" }
      : { bg: AN_COLORS.tint, bd: AN_COLORS.line, fg: AN_COLORS.ink2 };
  return (
    <span
      style={{
        display: "inline-flex", alignItems: "center", gap: 6, padding: "2px 9px", borderRadius: 99, fontSize: 11.5,
        fontWeight: 500, background: t.bg, color: t.fg, border: `1px solid ${t.bd}`, whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
}

// The endpoint's password-SSH state, in words.
function PasswordSshState({ ps, onRerunSetup }) {
  const s = String(ps.state || "").trim();
  if (ps.needsSetup || !s) {
    return (
      <Note tone="warn" icon={<AlertTriangle size={15} />}>
        <strong>Needs Re-run setup to enforce.</strong> Until it has, password SSH on this machine is left as it was.{" "}
        {onRerunSetup && (
          <Button type="link" size="small" style={{ padding: 0, height: "auto" }} onClick={onRerunSetup}>
            Re-run setup
          </Button>
        )}
      </Note>
    );
  }
  if (/^gateway-only/i.test(s)) {
    const allow = ps.allow || [];
    const except = allow.length ? allow.join(", ") : (s.match(/except\s+(.*)$/i) || [])[1];
    return (
      <div style={rowStyle}>
        <ShieldCheck size={16} color={AN_COLORS.success} />
        <span style={{ color: AN_COLORS.success, fontWeight: 500 }}>Password SSH only through the gateway</span>
        {except && <Chip>Direct password SSH allowed for: {except}</Chip>}
      </div>
    );
  }
  const reason = /^not applied:/i.test(s) ? s.slice(s.indexOf(":") + 1).trim() : s;
  return (
    <div style={{ ...rowStyle, alignItems: "flex-start" }}>
      <AlertTriangle size={16} color={AN_COLORS.danger} style={{ marginTop: 2 }} />
      <span style={{ color: AN_COLORS.danger }}>
        <strong>Not applied:</strong> {reason}
      </span>
    </div>
  );
}

// `embedded` drops the card and heading, for a dialog that has its own title.
// `onRerunSetup` (optional) offers Re-run setup when the rule isn't enforced.
export function PasswordSshPanel({ instanceId, readOnly, onRerunSetup, embedded = false }) {
  const isAdmin = isAdminUser();
  const [messageApi, msgCtx] = message.useMessage();
  const [ps, setPs] = useState(null); // null while loading
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState([]);
  const [saving, setSaving] = useState(false);
  const [applying, setApplying] = useState(false);
  const timer = useRef(null);

  const load = useCallback(() => {
    if (!instanceId) return;
    getPasswordSsh({
      instanceId: Number(instanceId),
      onSuccess: (res) => {
        setError("");
        setPs({ state: res.state || "", allow: res.allow || [], needsSetup: !!res.needsSetup });
      },
      onFailure: (err) => {
        setError(passwordErrorText(err, "Couldn't read password SSH."));
        setPs((p) => p || { state: "", allow: [], needsSetup: false, failed: true });
      },
    });
  }, [instanceId]);

  useEffect(() => {
    setPs(null);
    load();
  }, [load]);
  useEffect(() => () => clearTimeout(timer.current), []);

  const refresh = () => {
    clearTimeout(timer.current);
    setApplying(false);
    load();
  };

  const draftError = passwordSshAllowError(draft);
  const changed = JSON.stringify(draft) !== JSON.stringify(ps?.allow || []);

  const save = () => {
    setSaving(true);
    setPasswordSsh({
      instanceId: Number(instanceId),
      allow: draft,
      onSuccess: (res) => {
        setSaving(false);
        setEditing(false);
        setPs((p) => ({ ...p, allow: res.allow || draft }));
        if (res.scanJobId) {
          setApplying(true);
          clearTimeout(timer.current);
          timer.current = setTimeout(() => {
            setApplying(false);
            load();
          }, APPLY_WAIT_MS);
        } else {
          messageApi.info("Saved. It applies at the endpoint's next account scan — it has no gateway to scan from yet.");
        }
      },
      onFailure: (err) => {
        setSaving(false);
        messageApi.error(passwordErrorText(err, "Couldn't save the exceptions."));
      },
    });
  };

  const canEdit = isAdmin && !readOnly && ps && !ps.failed;

  const refreshButton = (
    <Button size="small" onClick={refresh} icon={<RefreshCw size={12} style={{ verticalAlign: -1, marginRight: 4 }} />}>
      Refresh
    </Button>
  );

  return (
    <div style={embedded ? undefined : { ...card, padding: 18 }}>
      {msgCtx}
      {embedded ? (
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 14 }}>
          <div style={{ fontSize: 12.5, color: AN_COLORS.muted, lineHeight: 1.5, fontFamily: AN_FONT }}>{PASSWORD_SSH_DESC}</div>
          {refreshButton}
        </div>
      ) : (
        <PanelHead eyebrow="Password SSH" title="Password logins to this machine" desc={PASSWORD_SSH_DESC} right={refreshButton} />
      )}
      {ps === null ? (
        <Spin size="small" />
      ) : (
        <div style={{ display: "grid", gap: 12, maxWidth: 680 }}>
          {error ? <Note tone="danger">{error}</Note> : <PasswordSshState ps={ps} onRerunSetup={onRerunSetup} />}
          {applying && (
            <Note tone="info" icon={<Loader size={15} />}>
              Applying… (within a minute). The endpoint's account scan writes the new rule; this re-reads on its own.
            </Note>
          )}
          {!ps.failed && (
            <div>
              <FieldLabel hint="Accounts that may still log in with a password directly, without the gateway. Root can never.">
                Exceptions
              </FieldLabel>
              {editing ? (
                <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                  <div style={{ flex: 1 }}>
                    <AccountsInput value={draft} onChange={setDraft} disabled={saving} placeholder="e.g. ubuntu" />
                    {draftError && <div style={{ color: AN_COLORS.danger, fontSize: 12, marginTop: 4 }}>{draftError}</div>}
                  </div>
                  <Button type="primary" loading={saving} disabled={!changed || !!draftError} onClick={save}>
                    Save
                  </Button>
                  <Button disabled={saving} onClick={() => setEditing(false)}>
                    Cancel
                  </Button>
                </div>
              ) : (
                <div style={rowStyle}>
                  {(ps.allow || []).length ? (
                    ps.allow.map((a) => <InlineValue key={a}>{a}</InlineValue>)
                  ) : (
                    <span style={{ color: AN_COLORS.muted, fontSize: 12.5 }}>None — no account logs in with a password directly.</span>
                  )}
                  {canEdit && (
                    <Button
                      type="link"
                      size="small"
                      style={{ padding: 0 }}
                      onClick={() => {
                        setDraft(ps.allow || []);
                        setEditing(true);
                      }}
                    >
                      Edit exceptions
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// "Password SSH exceptions…" from the endpoint details menu.
export function PasswordSshDialog({ open, instanceId, hostName, readOnly, onRerunSetup, onClose }) {
  return (
    <Modal
      open={open}
      title={hostName ? `Password SSH exceptions · ${hostName}` : "Password SSH exceptions"}
      onCancel={onClose}
      footer={<Button onClick={onClose}>Close</Button>}
      destroyOnClose
      width={640}
    >
      <div style={{ paddingTop: 4 }}>
        <PasswordSshPanel instanceId={instanceId} readOnly={readOnly} onRerunSetup={onRerunSetup} embedded />
      </div>
    </Modal>
  );
}
